import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faCheck } from '@fortawesome/free-solid-svg-icons';
import CgPopover from './CgPopover';

/**
 * CgCopyButton - A tiny hover-based copy button that shows a checkmark on success
 *
 * @param {Object} props
 * @param {string} props.textToCopy - The text to copy to clipboard
 * @param {Function} props.onClick - Optional callback when copy is triggered
 * @returns {JSX.Element}
 */
const CgCopyButton = ({ textToCopy, onClick }) => {
  const { t } = useTranslation('common');
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e) => {
    e.stopPropagation(); // Prevent event bubbling to parent row

    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);

      // Reset the checkmark after 2 seconds
      setTimeout(() => {
        setCopied(false);
      }, 2000);

      if (onClick) {
        onClick();
      }
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  return (
    <CgPopover
      triggerElement={
        <button
          onClick={handleCopy}
          className={`
            ml-1 p-0.5
            !opacity-0 group-hover:!opacity-100
            transition-all duration-200
            border-none bg-transparent
            cursor-pointer
            inline-flex items-center
            text-[10px]
            ${copied ? 'text-green-500' : 'text-gray-600 hover:text-primaryColor'}
          `}
        >
          <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
        </button>
      }
      popoverContent={
        <div className="text-xs p-1">
          {copied
            ? t('common:copyButton.copied', 'Copied!')
            : t('common:copyButton.copyToClipboard', 'Copy to clipboard')}
        </div>
      }
      position="top"
      trigger="hover"
      openDelay={300}
      closeDelay={0}
    />
  );
};

export default CgCopyButton;
